import { type GifData, tenorAPI, tenorConfig } from 'api/tenorApi.ts'
import { useCallback, useEffect, useState } from 'react'

interface UseGifSearchReturn {
  error: null | string
  gifs: GifData[]
  loading: boolean
  searchGifs: (query: string) => Promise<void>
}

export function useGifSearch(): UseGifSearchReturn {
  const [gifs, setGifs] = useState<GifData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | string>(null)

  const loadTrendingGifs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (!tenorConfig.isConfigured()) {
        throw new Error('Tenor API key is missing. Set TENOR_API_KEY')
      }
      const trendingGifs = await tenorAPI.getTrendingGifs(20)
      setGifs(trendingGifs)
    } catch (err) {
      setError('GIF search is unavailable: missing or invalid Tenor API key')
    } finally {
      setLoading(false)
    }
  }, [])

  const searchGifs = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      await loadTrendingGifs()
      return
    }

    setLoading(true)
    setError(null)
    try {
      if (!tenorConfig.isConfigured()) {
        throw new Error('Tenor API key is missing. Set TENOR_API_KEY')
      }
      const searchResults = await tenorAPI.searchGifs(searchQuery, 20)
      setGifs(searchResults)
    } catch (err) {
      setError('GIF search is unavailable: missing or invalid Tenor API key')
      console.error('Error searching GIFs:', err)
    } finally {
      setLoading(false)
    }
  }, [loadTrendingGifs])

  useEffect(() => {
    loadTrendingGifs()
  }, [loadTrendingGifs])

  return {
    error,
    gifs,
    loading,
    searchGifs
  }
}
